// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Total HVAC **electrical** load for a site, in one request — the measured
 * counterpart to the plan's combined-HVAC forecast (chillers, chilled-water
 * pumps, CRAHs). The BFF discovers the device set from org-scoped logical-node
 * membership and reads telemetry per *device*, because the logical-node
 * observations route returns nothing for these node types.
 *
 * Electrical kW only: thermal cooling output stays on the chiller-plant
 * endpoint, and unmetered DDC-cabinet auxiliary power is not in this total.
 */

import { get } from './api';
import { ENDPOINTS } from '../utils/constants';
import type { ScalarChart } from './chillerPlantService';

/**
 * One piece of equipment's part in the total, so a short sum is diagnosable:
 * `contributing: false` plus `failure` names which device is missing and why.
 */
export interface HvacMemberEntry {
  equipmentId: string;
  logicalNodeKey: string | null;
  profileKey: string | null;
  contributing: boolean;
  failure: string | null;
  current: number | null;
}

export interface HvacLoadCoverage {
  requested: number;
  contributing: number;
  unknown: string[];
  unresolved: string[];
  failed: string[];
  reasons: Record<string, unknown>;
}

export interface HvacLoadData {
  siteId: string;
  window: {
    start: string | null;
    end: string | null;
    resolution: string | null;
    sampleCadenceSeconds: number | null;
  };
  timestamps: string[];
  charts: {
    electricPower: ScalarChart;
  };
  members: HvacMemberEntry[];
  coverage: HvacLoadCoverage;
}

export interface HvacLoadEnvelope {
  data: HvacLoadData;
}

export interface HvacLoadQuery {
  /** Window start, ISO-8601 date-time. Defaults server-side to UTC midnight. */
  start?: string;
  /** Window end, ISO-8601 date-time. Defaults server-side to now. */
  end?: string;
  /**
   * Downsample the composed series (ISO-8601 duration, e.g. PT15M), applied
   * after summing so it cannot bias the total the way an upstream rollup would.
   */
  resolution?: string;
}

export const getHvacLoad = (
  siteId: string,
  orgId: string,
  { start, end, resolution }: HvacLoadQuery = {}
): Promise<HvacLoadEnvelope> => {
  const params = new URLSearchParams({ orgId });
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (resolution) params.set('resolution', resolution);

  return get<HvacLoadEnvelope>(`${ENDPOINTS.LIVE_HVAC_LOAD(siteId)}?${params.toString()}`);
};
